import {
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

/**
 * Tirer un message vers la droite pour y repondre.
 *
 * Le geste de toutes les messageries, et pour une bonne raison : il
 * designe le message et lance la reponse d'un seul mouvement, sans menu
 * ni cible a viser.
 *
 * # Ce qui le distingue d'un retour d'ecran
 *
 * Le balayage de retour part du bord gauche. Un message tire depuis le
 * bord serait donc les deux a la fois. On ignore les prises qui
 * commencent dans cette bande — c'est l'ecran qui gagne, il etait la
 * avant.
 *
 * # Ce qui le distingue d'un defilement
 *
 * Le fil se lit verticalement. Tant qu'on n'a pas franchement pris la
 * direction horizontale, on ne prend rien : le premier mouvement qui
 * penche vers le vertical rend la main a la liste, et le message ne
 * bougera plus jusqu'au relachement.
 *
 * # Ce qui le rend fluide
 *
 * Trois choses, apprises en le trouvant poussif.
 *
 * 1. **Aucune variable CSS pendant le geste, et une seule fleche pour
 *    tout le fil.** Ecrire une propriete personnalisee sur le message
 *    invalide tout ce qu'il contient a chaque frottement du doigt. Et
 *    donner sa fleche a chaque message revenait a poser cent elements
 *    masques dans la page — un masque se rasterise a part, et cent
 *    d'entre eux pesent sur tout ce qui defile, geste ou pas. Il n'y en
 *    a donc plus qu'une, partagee, posee a hauteur du message tenu : on
 *    n'en glisse jamais deux a la fois.
 * 2. **Une seule ecriture par image.** Un doigt emet bien plus
 *    d'evenements que l'ecran n'affiche d'images ; les poser toutes
 *    revenait a jeter le travail de la plupart. `requestAnimationFrame`
 *    n'en garde que la derniere.
 * 3. **Le pointeur est capture.** Un doigt rapide sort du message avant
 *    la fin du geste, et les evenements partaient alors a l'element du
 *    dessous : la course s'arretait en chemin, le message restait
 *    penche.
 *
 * # Ce qu'on ne lui demande pas
 *
 * **Aucune promotion en couche.** `will-change: transform` et
 * `translate3d` avaient ete poses par reflexe. Le fil defile derriere un
 * degrade de masque, et WebKit laissait alors le message peint a sa
 * place tandis que sa couche s'en allait : on voyait le message en
 * double, l'un glissant, l'autre immobile. Une translation en deux
 * dimensions suffit — le geste reste fluide, il l'etait deja pour les
 * trois raisons ci-dessus, qui ne touchent pas a la peinture.
 */

/**
 * En deca, le doigt n'a pas choisi sa direction.
 *
 * Bas volontairement : c'est avant que le systeme ne lance son
 * defilement qu'il faut avoir tranche, sinon les deux gestes courent
 * ensemble et le fil defile pendant qu'on tire un message.
 */
const EVEIL_PX = 6;
/** Au-dela, la reponse part au relachement. */
const DECLENCHE_PX = 56;
/**
 * La resistance passe le seuil.
 *
 * Le message continue de suivre le doigt aussi loin qu'on l'emmene, mais
 * a cette fraction de sa vitesse : il n'y a pas de butee, seulement une
 * elasticite. Le lacher le ramene chez lui.
 */
const RESISTANCE = 0.4;
/** La bande du bord gauche appartient au retour d'ecran. */
const BORD_PX = 28;

export function useGlisserPourRepondre(
  onRepondre: () => void,
  /** Appele des que le geste prend, pour que l'appui long lache prise. */
  onPris?: () => void,
) {
  /*
    Ce qui empeche le fil de defiler pendant qu'on tire un message.

    `touch-action: pan-y` laisse le systeme paner verticalement, et il le
    fait meme quand notre geste a pris : on tirait un message et la
    conversation defilait sous lui. Un `touchmove` non passif, pose pour
    la duree du geste seulement, refuse le mouvement des que l'horizontale
    l'emporte. Pose sur le document et en capture, pour arriver avant qui
    que ce soit d'autre — et retire au relachement, pour ne rien couter
    le reste du temps.
  */
  const barrage = useRef<((evenement: TouchEvent) => void) | null>(null);

  function poserLeBarrage(actif: () => boolean) {
    if (barrage.current) return;
    const bloquer = (evenement: TouchEvent) => {
      if (actif() && evenement.cancelable) evenement.preventDefault();
    };
    barrage.current = bloquer;
    document.addEventListener('touchmove', bloquer, { passive: false, capture: true });
  }

  function leverLeBarrage() {
    if (!barrage.current) return;
    document.removeEventListener('touchmove', barrage.current, { capture: true });
    barrage.current = null;
  }

  const noeud = useRef<HTMLElement | null>(null);
  /*
    La fleche partagee, retrouvee une fois par geste et non par image.

    Elle vit dans l'ecran, pas dans le message — c'est ce qui evite d'en
    avoir une par bulle. La chercher dans la page est le prix de ce
    partage : une lecture au moment ou le doigt prend, jamais pendant
    qu'il glisse.
  */
  const fleche = useRef<HTMLElement | null>(null);
  const prise = useRef<{ x: number; y: number; pris: boolean; abandon: boolean } | null>(null);
  /*
    Le clic qui suit un geste pris, a avaler.

    Un `pointerup` est suivi d'un `click`, dispatche a ce qu'il y a sous
    le doigt. Tant que le message ne portait qu'un petit visage, on
    tombait rarement dessus ; une carte de post occupe toute la bulle, et
    tirer pour repondre partait alors ouvrir le post.

    Remis a plat a chaque nouvelle prise : si le clic attendu n'arrive
    jamais — le doigt a fini hors de toute cible — le drapeau ne doit pas
    manger celui d'apres.
  */
  const clicAAvaler = useRef(false);
  /* L'image demandee et pas encore rendue : on n'en garde qu'une. */
  const image = useRef<number | null>(null);
  const vise = useRef(0);

  function peindre() {
    image.current = null;
    const n = noeud.current;
    if (!n) return;
    const distance = vise.current;
    n.style.transform = distance ? `translateX(${distance}px)` : '';
    /* Posee sur l'ecran et non dans le message, elle ne bouge pas avec
       lui : il n'y a que son opacite a suivre. */
    const f = fleche.current;
    if (f) f.style.opacity = String(Math.min(1, distance / DECLENCHE_PX));
  }

  function viser(distance: number) {
    vise.current = distance;
    if (image.current === null) image.current = requestAnimationFrame(peindre);
  }

  function relacher(anime: boolean) {
    if (image.current !== null) {
      cancelAnimationFrame(image.current);
      image.current = null;
    }
    const n = noeud.current;
    const f = fleche.current;
    if (n) {
      /* Le retour prend son temps : reparti en dix-huit centiemes, il
         claquait plus qu'il ne revenait. */
      n.style.transition = anime ? 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)' : '';
      n.style.transform = '';
    }
    if (f) {
      f.style.transition = anime ? 'opacity 0.3s ease-out' : '';
      f.style.opacity = '0';
    }
    fleche.current = null;
    vise.current = 0;
  }

  return {
    ref: noeud,
    fleche,
    handlers: {
      onPointerDown(event: ReactPointerEvent<HTMLElement>) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.clientX < BORD_PX) return;
        clicAAvaler.current = false;
        const p = { x: event.clientX, y: event.clientY, pris: false, abandon: false };
        prise.current = p;
        /* Pose des le premier contact : refuser un `touchmove` une fois
           le defilement lance ne l'arrete plus. */
        if (event.pointerType === 'touch') poserLeBarrage(() => p.pris);
      },
      onPointerMove(event: ReactPointerEvent<HTMLElement>) {
        const p = prise.current;
        if (!p || p.abandon) return;

        const dx = event.clientX - p.x;
        const dy = event.clientY - p.y;

        if (!p.pris) {
          if (Math.abs(dx) < EVEIL_PX && Math.abs(dy) < EVEIL_PX) return;
          /* Vertical, ou vers la gauche : ce n'est pas pour nous. */
          if (Math.abs(dy) >= Math.abs(dx) || dx <= 0) {
            p.abandon = true;
            return;
          }
          p.pris = true;
          onPris?.();

          /*
            Tout ce qui se dit une fois se dit ici, au moment ou le geste
            prend : la transition qui gene, la promesse faite au
            compositeur, et la capture du pointeur. Le repeter a chaque
            frottement, c'etait payer trois fois par image.
          */
          const n = noeud.current;
          if (n) {
            n.style.transition = '';

            /* Elle se pose a hauteur du message tenu, au bord gauche de
               l'ecran — la meme place pour ce qu'on recoit et pour le
               sien, comme sur les autres messageries. */
            const f = document.querySelector<HTMLElement>('.ms-fleche');
            if (f) {
              const cadre = n.getBoundingClientRect();
              f.style.transition = '';
              f.style.top = `${cadre.top + cadre.height / 2 - 12}px`;
              fleche.current = f;
            }
          }
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }

        /*
          La course s'amortit : passe le seuil, le message continue de
          suivre le doigt mais de moins en moins. Sans cela il partait a
          l'autre bout de l'ecran et le geste perdait sa mesure.
        */
        const suivi =
          dx <= DECLENCHE_PX ? dx : DECLENCHE_PX + (dx - DECLENCHE_PX) * RESISTANCE;

        /*
          Un elastique, pas une glissiere.

          Il y avait une butee a l'arrivee : le message s'arretait net et
          le doigt continuait sans lui. Il suit maintenant aussi loin
          qu'on l'emmene, de plus en plus a contrecoeur passe le seuil,
          et le lacher le ramene chez lui.

          Reste la borne du bas, elle indispensable : revenir sur ses pas
          emmenait le message au-dela de sa place, vers la gauche, jusqu'a
          le sortir de l'ecran. Le geste ne va que dans un sens — on peut
          le defaire, pas le poursuivre de l'autre cote.
        */
        viser(Math.max(0, suivi));
      },
      onPointerUp(event: ReactPointerEvent<HTMLElement>) {
        const p = prise.current;
        prise.current = null;
        leverLeBarrage();
        if (!p?.pris) return;
        clicAAvaler.current = true;
        relacher(true);
        if (event.clientX - p.x >= DECLENCHE_PX) onRepondre();
      },
      onClickCapture(event: ReactMouseEvent<HTMLElement>) {
        if (!clicAAvaler.current) return;
        clicAAvaler.current = false;
        event.preventDefault();
        event.stopPropagation();
      },
      onPointerCancel() {
        prise.current = null;
        leverLeBarrage();
        relacher(true);
      },
    },
  };
}
